import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { isCapacitorNative, signInWithGoogleNative, initializeGoogleAuth, getApiBaseUrl } from '@/lib/capacitor';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme: string;
              size: string;
              width: number;
            }
          ) => void;
        };
      };
    };
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNative] = useState(() => isCapacitorNative());
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);

  const redirectTo = searchParams.get('redirect') || '/share';

  // Initialize native Google Auth on mount
  useEffect(() => {
    if (isNative) {
      initializeGoogleAuth();
    }
  }, [isNative]);

  // Fetch Google Client ID from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/auth/config`);
        const data = await response.json();
        if (data.success && data.googleClientId) {
          setGoogleClientId(data.googleClientId);
        } else {
          setError('Google Client ID not configured');
        }
      } catch (err) {
        console.error('Failed to fetch auth config:', err);
        setError('Failed to load authentication configuration');
      }
    };
    
    if (!isNative) {
      fetchConfig();
    }
  }, [isNative]);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('ping_access_token');
    if (token) {
      navigate(redirectTo);
      return;
    }

    // Only load GIS script for web, and only after we have the client ID
    if (isNative || !googleClientId) {
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleWebGoogleResponse,
      });

      const buttonDiv = document.getElementById('google-signin-button');
      if (buttonDiv) {
        window.google?.accounts.id.renderButton(buttonDiv, {
          theme: 'filled_black',
          size: 'large',
          width: 280,
        });
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [navigate, redirectTo, isNative, googleClientId]);

  // Handle web Google Identity Services response
  const handleWebGoogleResponse = async (response: { credential: string }) => {
    await authenticateWithBackend(response.credential);
  };

  // Handle native Google Sign-In button click
  const handleNativeSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signInWithGoogleNative();
      await authenticateWithBackend(result.idToken);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      // Don't show error for user cancellation
      if (!message.includes('cancelled') && !message.includes('canceled')) {
        setError(message);
      }
      setIsLoading(false);
    }
  };

  // Common backend authentication logic
  const authenticateWithBackend = async (idToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: idToken }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('ping_access_token', data.accessToken);
      localStorage.setItem('ping_refresh_token', data.refreshToken);
      localStorage.setItem('ping_user', JSON.stringify(data.user));

      navigate(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-primary/5">
      <div className="w-full max-w-sm space-y-8 animate-in">
        {/* Logo/Title */}
        <div className="text-center space-y-2">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center glow">
            <span className="text-4xl">📡</span>
          </div>
          <h1 className="text-3xl font-display font-bold gradient-text">
            Ping
          </h1>
          <p className="text-muted-foreground">
            Sign in to share with the display
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Google Sign In */}
        <div className="flex justify-center">
          {isLoading ? (
            <Button disabled className="w-full max-w-[280px]">
              Signing in...
            </Button>
          ) : isNative ? (
            // Native sign-in button
            <Button
              onClick={handleNativeSignIn}
              className="w-full max-w-[280px] h-12 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>
          ) : (
            // Web GIS button container
            <div id="google-signin-button" />
          )}
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Only authorized accounts can access this app
        </p>
      </div>
    </div>
  );
}
